import React from "react";

/**
 * Shared multi-day chevron banner for calendar block rendering.
 *
 * Phase 0 of the calendar-event-types plan (see
 * `bdr_mobile_app/planning/calendar_event_types_2026_04_22/CALENDAR_EVENT_TYPES_MERGED_PLAN.md §7.1`)
 * extracts the duplicated banner JSX from `WeekModal.jsx:907`, `DayModal.jsx:396`,
 * and `DayViewModal.jsx:894` into one component. Phase 5/6 will add chain-link
 * icon rendering alongside the chevron using the `isChained` prop (currently a
 * no-op — accepted so callers don't need a second prop update when Phase 5 lands).
 *
 * Cross-platform glyph parity: U+2192 →, U+2190 ←, U+2194 ↔.
 * Mobile's `chevronPrefix` helper in `bdr_mobile_app/lib/components/calendar/
 * multi_day_banner.dart` emits the identical glyphs.
 *
 * Note: this component relies on CSS classes defined in the parent modal's CSS
 * (`weekModal.css:177` for variant="week", `dayModal.css:178` for variant="day").
 * Consolidation into a local CSS module is a Phase 7 polish item — not needed
 * for correctness today.
 */
export default function MultiDayBanner({
  isFirstDay,
  isLastDay,
  workDayIndex,
  totalWorkDays,
  // Phase 6: chain-link icon rendering. Vacation is excluded defensively
  // per §6.12 even though the lane layout's `isChained` already filters it.
  isChained = false,
  eventType = "job",
  variant = "week",
}) {
  const chevron = isFirstDay ? "→" : isLastDay ? "←" : "↔";
  const className =
    variant === "day"
      ? "day-grid-block-multiday-banner"
      : "week-grid-block-multiday-banner";
  const title =
    workDayIndex != null && totalWorkDays != null
      ? `Day ${workDayIndex + 1} of ${totalWorkDays}`
      : undefined;
  const showChainIcon = isChained && eventType !== "vacation";

  return (
    <div className={className} title={title}>
      {chevron}
      {showChainIcon && (
        <svg
          aria-label="Chained event"
          viewBox="0 0 24 24"
          width={11}
          height={11}
          style={{ marginLeft: 4, verticalAlign: "middle", fill: "currentColor" }}
        >
          {/* Material-style link glyph; matches the mobile `Icons.link` visual
              so the chain indicator looks identical across platforms. */}
          <path d="M10.59 13.41a1 1 0 0 0 1.41 0l4.24-4.24a3 3 0 1 0-4.24-4.24L10.29 6.64a1 1 0 1 0 1.41 1.41l1.71-1.71a1 1 0 0 1 1.41 1.41l-4.24 4.24a1 1 0 0 0 0 1.42Zm2.82-2.82a1 1 0 0 0-1.41 0L7.76 14.83a3 3 0 0 0 4.24 4.24l1.71-1.71a1 1 0 1 0-1.41-1.41l-1.71 1.71a1 1 0 0 1-1.41-1.41l4.24-4.24a1 1 0 0 0 0-1.42Z" />
        </svg>
      )}
    </div>
  );
}
